import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// Cache pokusů o dotažení (viz doc/plans/fill-attempt-cache.md).
//
// Po KAŽDÉM runToFill pokusu pro dané okno se zapíše record(klíč, now). Dokud
// neuplyne T minut, klient okno znovu NENABÍZÍ (do dialogu ani do fronty). Cache
// NENÍ predikce „kdy data u zdroje dozrají" — je to zdvořilostní pauza mezi fill
// POSTy (fill jde na Oracle / stažení CHMI, nechceme ho opakovat každých pár
// sekund při re-renderech / prokliku stránek).
//
// isBlocked() je jeden ze tří zdrojů, které useToFill kontroluje před nabídnutím
// (spolu s useFillQueueStore.has / useFillPromptStore.has) — položka se nikdy
// nenabídne 2×. Tenhle store je navíc perzistentní (localStorage), nezávislý na
// tom, jestli je obrazovka pořád otevřená.

// minuty
const FILL_RETRY_MIN = {
  live: {   // okno končí ~teď — data můžou dorazit každou chvíli, zkoušej svižně
    '10M': 5, 'HLY': 10, 'DLY': 30, 'MONTH': 60, 'YEAR': 60,
  },
  closed: { // celé okno v minulosti — data se (skoro) nemění, šetři zdroj
    '10M': 60, 'HLY': 120, 'DLY': 360, 'MONTH': 720, 'YEAR': 1440,
  },
}
const GC_AGE = 2 * 24 * 60 * 60_000   // 2 dny
const MAX_ENTRIES = 500

// Krok granularity v ms — pro detekci „živého" okna (konec ~teď).
export const GRAN_STEP_MS = {
  '10M':  10 * 60_000,
  'HLY':  60 * 60_000,
  'DLY':  24 * 60 * 60_000,
  'MONTH': 31 * 24 * 60 * 60_000,
  'YEAR':  366 * 24 * 60 * 60_000,
}

// live = okno končí ~teď. isAktual (klouzavé okno) → vždy živé. Jinak porovnej
// konec okna s „teď minus krok granularity".
export const isLiveWindow = (gran, isAktual, endDateMs) => {
  if (isAktual) return true
  if (endDateMs == null) return false
  return endDateMs > Date.now() - (GRAN_STEP_MS[gran] ?? GRAN_STEP_MS['10M'])
}

const dataEndpoint = (ep) => (ep ?? '').replace(/\/fill$/, '')

// item = to_fill položka. params.begin_date/end_date jsou serverem normalizované
// (floor10Min / startOfDayUtc / …) — deterministické, stejné hodnoty jdou do POST /fill.
export const attemptKey = (item) =>
  `${dataEndpoint(item.endpoint)}|${item.params?.gh_id}|${item.params?.ts_id}` +
  `|${item.params?.begin_date}|${item.params?.end_date}`

const ttlMs = (gran, live) =>
  (FILL_RETRY_MIN[live ? 'live' : 'closed'][gran] ?? FILL_RETRY_MIN.closed['10M']) * 60_000

export const useFillAttemptStore = create(persist((set, get) => ({
  // { [key]: { at: <ms>, gran: '10M'|…, live: bool } }
  entries: {},

  // true = pro tohle okno se právě zkoušel fill, ještě neuplynulo T → NEnabízet
  isBlocked: (key) => {
    const e = get().entries[key]
    if (!e) return false
    return Date.now() - e.at < ttlMs(e.gran, e.live)
  },

  // Volá se po KAŽDÉM runToFill pokusu pro okno (filled i not filled).
  record: (key, { gran, live } = {}) => set((s) => {
    const now = Date.now()
    const entries = { ...s.entries, [key]: { at: now, gran, live } }
    for (const [k, v] of Object.entries(entries))
      if (now - v.at > GC_AGE) delete entries[k]
    const keys = Object.keys(entries)
    if (keys.length > MAX_ENTRIES) {
      keys.sort((a, b) => entries[a].at - entries[b].at)
      for (const k of keys.slice(0, keys.length - MAX_ENTRIES)) delete entries[k]
    }
    return { entries }
  }),

  // Ruční „Dotáhnout" — uživatel chce teď, i když T ještě neuplynulo.
  invalidate: (key) => set((s) => {
    if (!(key in s.entries)) return s
    const next = { ...s.entries }
    delete next[key]
    return { entries: next }
  }),

  clear: () => set({ entries: {} }),
}), { name: 'fill-attempts' }))
